import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Tab, Tabs } from '@mui/material';
import { useTranslate } from 'react-admin';
import marketplaceService, { extractErrorMessage, MarketplaceListing } from '../../../services/marketplaceService';
import tokenService from '../../../services/tokenService';
import {
    allowedLicenseTypes,
    partitionByOrigin,
    SellerVisibilityKey,
    showGroupTab,
    totalPriceCents,
    visibilityMeta,
} from '../../../pages/marketplaceVisibility';
import MaterialSymbol from '../../common/MaterialSymbol';
import CheckoutDialog from '../Checkout/CheckoutDialog';
import ListingCard, { DIFFICULTY_KEYS } from './ListingCard';
import '../marketplace-design.css';

const matchesQuery = (listing: MarketplaceListing, query: string): boolean => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [listing.title, listing.description ?? '', ...(listing.tags ?? [])].some((part) => part.toLowerCase().includes(q));
};

type SortKey = 'new' | 'name' | 'price';
const LEVELS = ['beginner', 'intermediate', 'advanced'] as const;

/** Günstigster angebotener Preis (Cent) für die Sortierung „Preis aufsteigend“. */
const minPriceCents = (listing: MarketplaceListing): number => {
    const types = allowedLicenseTypes(listing);
    return types.length === 0 ? Number.MAX_SAFE_INTEGER : Math.min(...types.map((t) => totalPriceCents(listing, t, 1)));
};

const sortListings = (list: MarketplaceListing[], sort: SortKey): MarketplaceListing[] => {
    const copy = [...list];
    if (sort === 'name') return copy.sort((a, b) => a.title.localeCompare(b.title, 'de'));
    if (sort === 'price') return copy.sort((a, b) => minPriceCents(a) - minPriceCents(b));
    return copy.sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));
};

/**
 * Marktplatz durchsuchen (Plan 3.4): zwei getrennte Bereiche statt Chip-Filter —
 * „Öffentlicher Marktplatz“ und „Angebote meiner Unternehmensgruppe“. Der
 * Gruppen-Bereich erscheint nur, wenn der Token eine Unternehmensgruppe trägt.
 * Jedes Angebot liegt in genau einem Bereich (nach Herkunft).
 */
const BuyerBrowsePanel: React.FC<{ canBuy: boolean }> = ({ canBuy }) => {
    const translate = useTranslate();
    const hasGroup = showGroupTab(tokenService.hasCompanyGroup?.() ?? false);

    const [listings, setListings] = useState<MarketplaceListing[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [scope, setScope] = useState<SellerVisibilityKey>('public');
    const [query, setQuery] = useState('');
    const [level, setLevel] = useState('all');
    const [sort, setSort] = useState<SortKey>('new');
    const [checkoutListing, setCheckoutListing] = useState<MarketplaceListing | null>(null);

    // `silent`: Aktualisierung nach dem Kauf, ohne den offenen Dialog abzubauen
    const load = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        setError(null);
        try {
            setListings(await marketplaceService.browse());
        } catch (err) {
            setError(extractErrorMessage(err, translate('marketplaceBuyer.loadError')));
        } finally {
            setLoading(false);
        }
    }, [translate]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const parts = useMemo(() => partitionByOrigin(listings), [listings]);
    const activeScope: SellerVisibilityKey = hasGroup ? scope : 'public';
    const visible = sortListings(
        parts[activeScope].filter((l) => matchesQuery(l, query) && (level === 'all' || l.course.difficulty === level)),
        sort,
    );

    if (loading) {
        return <div className="mk-skel" aria-busy="true" />;
    }

    return (
        <div>
            {error && (
                <div className="mk-alert err" role="alert">
                    <MaterialSymbol name="error" />
                    <div className="body">{error}</div>
                </div>
            )}

            {hasGroup && (
                <div className="mk-scope-row">
                    <Tabs
                        value={scope}
                        onChange={(_, value: SellerVisibilityKey) => setScope(value)}
                        variant="scrollable"
                        scrollButtons={false}
                        aria-label={translate('marketplaceBuyer.tabs.ariaLabel')}
                        className="mk-seg"
                    >
                        {(['public', 'group'] as const).map((key) => (
                            <Tab
                                key={key}
                                value={key}
                                icon={<MaterialSymbol name={visibilityMeta(key).icon} size={18} />}
                                iconPosition="start"
                                disableRipple
                                label={
                                    <>
                                        {translate(`marketplaceBuyer.tabs.${key}`)} <span className="mk-n">{parts[key].length}</span>
                                    </>
                                }
                            />
                        ))}
                    </Tabs>
                </div>
            )}

            <p className="mk-scope-hint">{translate(`marketplaceBuyer.scopeHint.${activeScope}`)}</p>

            <div className="mk-toolbar">
                <div className="mk-find">
                    <MaterialSymbol name="search" />
                    <input
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={translate('marketplaceBuyer.search')}
                        aria-label={translate('marketplaceBuyer.search')}
                        autoComplete="off"
                    />
                </div>
                <div className="mk-selw">
                    <select value={level} onChange={(e) => setLevel(e.target.value)} aria-label={translate('marketplaceBuyer.filter.level')}>
                        <option value="all">{translate('marketplaceBuyer.filter.allLevels')}</option>
                        {LEVELS.map((l) => (
                            <option key={l} value={l}>{translate(DIFFICULTY_KEYS[l])}</option>
                        ))}
                    </select>
                    <MaterialSymbol name="expand_more" className="chev" />
                </div>
                <div className="mk-selw">
                    <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)} aria-label={translate('marketplaceBuyer.filter.sort')}>
                        <option value="new">{translate('marketplaceBuyer.filter.sortNew')}</option>
                        <option value="name">{translate('marketplaceBuyer.filter.sortName')}</option>
                        <option value="price">{translate('marketplaceBuyer.filter.sortPrice')}</option>
                    </select>
                    <MaterialSymbol name="expand_more" className="chev" />
                </div>
                <span className="mk-pill neu" style={{ marginLeft: 'auto' }}>
                    {translate('marketplaceBuyer.netPayPalNote')}
                </span>
            </div>

            {visible.length === 0 ? (
                <div className="mk-empty" role="status">
                    <MaterialSymbol name="search_off" />
                    <p>{translate(`marketplaceBuyer.empty.${activeScope}`)}</p>
                </div>
            ) : (
                <div className="mk-ogrid">
                    {visible.map((listing) => (
                        <ListingCard key={listing.id} listing={listing} canBuy={canBuy} onLicense={setCheckoutListing} />
                    ))}
                </div>
            )}

            <CheckoutDialog
                open={checkoutListing !== null}
                listing={checkoutListing}
                onClose={() => setCheckoutListing(null)}
                onCompleted={() => load(true)}
            />
        </div>
    );
};

export default BuyerBrowsePanel;
